import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { DetailedActiveAlertDTO } from '../alerts/models/alerts.model';
import {
  AmlRiskRatingDTO,
  AmlRiskRatingHistoryDTO,
  EDDProcessDTO,
  EnablePlayerDto,
  InAppMessageDTO,
  MessageTemplate,
  MessageTemplateLabel,
  MessageTemplatesLabels,
  OverrideRatingDTO,
  PlayerAffordabilityJourney,
  PlayerCardDTO,
  PlayerIdentityDTO,
  PlayerJourneyDTO,
  PlayerJourneyType,
  PlayerRestrictionsDTO,
  RemoveJourneyDTO,
  SentInAppMessage,
  StartEDDProcessDTO,
  TransUnionEntry,
} from './player-details.model';
import { map } from 'rxjs/operators';
import { Page } from '../../../../common/paging-dto';
import { NextThresholdsDTO } from '../../../../components/data-type-widgets/data-type/backoffice/affordability-settings/affordability-settings.model';
import {
  CreateOccupation,
  OccupationRequestDTO,
  OccupationSuggestion,
  PlayerOccupation,
} from './components/occupation/model/occupation.model';

@Injectable({
  providedIn: 'root',
})
export class PlayersDetailsService {
  private readonly refreshSubject = new Subject<void>();
  readonly refresh$ = this.refreshSubject.asObservable();

  constructor(private http: HttpClient) {}

  refresh(): void {
    this.refreshSubject.next();
  }

  public getHeaderData(playerId: number) {
    return this.http.get<any>(`/player/${playerId}/header`);
  }

  public getActiveAlerts(playerId?: string): Observable<DetailedActiveAlertDTO[]> {
    const params = {
      'player-id': parseInt(playerId, 10),
    };
    return this.http.get<DetailedActiveAlertDTO[]>('/player-activity/api/player-alerts/active', {
      params,
    });
  }

  public getActiveCards(playerId: string): Observable<PlayerCardDTO[]> {
    return this.http.get<PlayerCardDTO[]>(`/player/api/players/${playerId}/cards?statuses=ACTIVE`);
  }

  public getInAppMessageTemplateLabels(): Observable<MessageTemplateLabel[]> {
    return this.http
      .get<MessageTemplatesLabels>(`/player/api/backoffice/in-app-messages/templates`)
      .pipe(map((e) => e.labels));
  }

  public getMessageTemplateById(templateId: string, firstName: string): Observable<MessageTemplate> {
    return this.http.get<MessageTemplate>(`/player/api/backoffice/in-app-messages/templates/${templateId}`, {
      params: { firstName },
    });
  }

  public sendForceOccupationCollection(playerId: string): Observable<void> {
    return this.http.post<void>(`/player/api/backoffice/players/${playerId}/occupation-collection/force`, {});
  }

  public sendCancelOccupationCollectionRequest(playerId: string): Observable<void> {
    return this.http.post<void>(`/player/api/backoffice/players/${playerId}/occupation-collection/cancel`, {});
  }

  public getActiveOccupationRequest(playerId: number) {
    return this.http.get<OccupationRequestDTO>(`/player/api/backoffice/players/${playerId}/occupation-collection`);
  }

  public getCurrentOccupation(playerId: number) {
    return this.http.get<PlayerOccupation>(`/player/api/backoffice/players/${playerId}/occupation`);
  }

  public saveOccupation(playerId: number, request: CreateOccupation) {
    return this.http.post(`/player/api/backoffice/players/${playerId}/occupation`, request);
  }

  public getIdentity(playerId: string): Observable<PlayerIdentityDTO> {
    return this.http.get<PlayerIdentityDTO>(`/player/api/players/${playerId}/identity`);
  }

  public sendInAppMessage(playerId: string, req: InAppMessageDTO): Observable<void> {
    return this.http.post<void>(`/player/api/backoffice/players/${playerId}/in-app-messages`, req);
  }

  public getInAppMessages(pageNumber: number, pageSize: number, playerId: string): Observable<Page<SentInAppMessage>> {
    let params = new HttpParams().set('pageNumber', pageNumber.toString()).set('pageSize', pageSize.toString());
    return this.http.get<Page<SentInAppMessage>>(`/player/api/backoffice/players/${playerId}/in-app-messages`, {
      params,
    });
  }

  public enablePlayer(playerId: number, dto: EnablePlayerDto): Observable<void> {
    return this.http.post<void>(`/player/v2/${playerId}/enable`, dto);
  }

  public getTransUnionEntries(playerId: string): Observable<TransUnionEntry[]> {
    return this.http.get<TransUnionEntry[]>(`/player/api/backoffice/players/${playerId}/trans-union`);
  }

  public checkPlayerInTransUnion(playerId: string): Observable<TransUnionEntry> {
    return this.http.post<TransUnionEntry>(`/player/api/backoffice/players/${playerId}/trans-union`, {});
  }

  public getPlayersAmlRisk(playerId: number): Observable<AmlRiskRatingDTO> {
    return this.http.get<AmlRiskRatingDTO>(`/player/api/backoffice/players/${playerId}/aml-risk-rating`);
  }

  public getPlayersAmlRiskHistory(playerId: number): Observable<AmlRiskRatingHistoryDTO[]> {
    return this.http.get<AmlRiskRatingHistoryDTO[]>(
      `/player/api/backoffice/players/${playerId}/aml-risk-rating/history`
    );
  }

  public getPlayerOccupationHistory(playerId: number): Observable<PlayerOccupation[]> {
    return this.http.get<PlayerOccupation[]>(`/player/api/backoffice/players/${playerId}/occupation/history`);
  }

  public overrideRisk(playerId: number, dto: OverrideRatingDTO): Observable<AmlRiskRatingDTO> {
    return this.http.post<AmlRiskRatingDTO>(`/player/api/backoffice/players/${playerId}/aml-risk-rating/override`, dto);
  }

  public getPlayersNextEDDThresholds(playerId: number): Observable<NextThresholdsDTO> {
    return this.http.get<NextThresholdsDTO>(`/player-activity/api/edd/${playerId}/next-thresholds`);
  }

  public getOccupationSuggestions(query: string): Observable<OccupationSuggestion[]> {
    const params = query !== '' ? new HttpParams().set('query', query) : {};
    return this.http.get<OccupationSuggestion[]>(`/player/api/backoffice/occupations`, {
      params: params,
    });
  }

  public isPlayerDuringEDDProcess(playerId: number): Observable<EDDProcessDTO> {
    return this.http.get<EDDProcessDTO>(`/player-activity/api/edd-process/player/${playerId}/active`);
  }

  public startEDDProcess(playerId: number, output: any): Observable<void> {
    const req: StartEDDProcessDTO = {
      reason: output.reason,
      createJiraTicket: output.createJiraTicket,
      setWithdrawWatch: output.setWithdrawWatch,
      createAccountTimer: output.createAccountTimer,
      createDocReq: output.createDocReq,
    };
    return this.http.post<void>(`/player-activity/api/edd-process/player/${playerId}`, req);
  }

  public finishEDDProcess(playerId: number): Observable<void> {
    return this.http.post<void>(`/player-activity/api/edd-process/player/${playerId}/finish`, {});
  }

  public getAffordabilityJourneys(playerId: number): Observable<PlayerAffordabilityJourney[]> {
    return this.http.get<PlayerAffordabilityJourney[]>(
      `/player-activity/api/backoffice/player-restriction/${playerId}`
    );
  }

  public getPlayerRestrictions(playerId: number): Observable<PlayerRestrictionsDTO> {
    const params = new HttpParams().set('player-id', playerId);
    return this.http.get<PlayerRestrictionsDTO>(`/player-activity/api/backoffice/player-journey`, { params });
  }

  public getActiveJourney(playerId: number, type: PlayerJourneyType) {
    const params = new HttpParams().set('player-id', playerId).set('journey-type', type);
    return this.http.get<PlayerJourneyDTO>(`/player-activity/api/backoffice/player-journey/details`, { params });
  }

  public finishJourney(playerId: number, request: RemoveJourneyDTO): Observable<string> {
    const params = new HttpParams().set('player-id', playerId);

    return this.http.post<string>(`/player-activity/api/backoffice/player-journey/finish`, request, {
      params,
    });
  }
}
